import { Link } from "react-router-dom";
import { useT } from "@/context/LanguageContext";
import { useCatalog } from "@/context/CatalogContext";
import { useSiteContent } from "@/context/SiteContentContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const IG = <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>;
const TT = <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.6 6.6a5.4 5.4 0 0 1-3.3-1.1 5.4 5.4 0 0 1-2.2-3.4h-3v13.4a2.6 2.6 0 1 1-2.6-2.6c.3 0 .5 0 .8.1V9.9a5.9 5.9 0 1 0 4.8 5.7V9.2a8.4 8.4 0 0 0 5.5 2z"/></svg>;

const Footer = () => {
  const { t } = useT();
  const { availableCategories } = useCatalog();
  const { journalCount, settings } = useSiteContent();
  const threshold = settings?.free_shipping_threshold ?? 150;
  const year = new Date().getFullYear();

  return (
    <footer className="mt-24 md:mt-32 bg-[#2C2C2C] text-[#F1ECE2]">
      {/* Link columns — kept intentionally lean: brand + 3 utility columns */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-16 grid grid-cols-2 md:grid-cols-5 gap-10 md:gap-8">
        <div className="col-span-2">
          <Link to="/" className="inline-block" data-testid="footer-brand-link">
            <div className="font-serif-display text-2xl tracking-[0.14em]">GOYA</div>
            <div className="font-typewriter text-[0.6rem] tracking-[0.55em] opacity-75 mt-1">— LISBOA —</div>
          </Link>
          <p className="mt-6 max-w-sm text-sm text-[#F1ECE2]/70 leading-relaxed">{t("footer.tagline")}</p>
          <div className="mt-6 flex items-center gap-3">
            <a href="https://www.instagram.com/goyalisboa" target="_blank" rel="noreferrer" aria-label="Instagram — @goyalisboa" data-testid="footer-social-ig" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[var(--gl-orange)] hover:text-[var(--gl-orange-text)] flex items-center justify-center transition">{IG}</a>
            <a href="https://www.tiktok.com/@goya.lisboa" target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="footer-social-tt" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[var(--gl-orange)] hover:text-[var(--gl-orange-text)] flex items-center justify-center transition">{TT}</a>
          </div>
        </div>

        {/* Shop — dynamic per available inventory */}
        <div>
          <div className="font-typewriter text-[10px] tracking-[0.3em] uppercase text-[#F1ECE2]/60 mb-4">{t("footer.col.shop")}</div>
          <ul className="space-y-2 text-sm" data-testid="footer-shop-links">
            <li><Link to="/collection/all" className="hover:text-[var(--gl-orange)] transition">{t("nav.newArrivals")}</Link></li>
            {availableCategories.map((c) => (
              <li key={c.slug}>
                <Link to={c.to} className="hover:text-[var(--gl-orange)] transition">{c.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Help — essentials only */}
        <div>
          <div className="font-typewriter text-[10px] tracking-[0.3em] uppercase text-[#F1ECE2]/60 mb-4">{t("footer.col.help")}</div>
          <ul className="space-y-2 text-sm" data-testid="footer-help-links">
            <li><Link to="/help/contact" className="hover:text-[var(--gl-orange)] transition">{t("footer.help.contact")}</Link></li>
            <li><Link to="/help/faq" className="hover:text-[var(--gl-orange)] transition">{t("footer.help.faq")}</Link></li>
            <li><Link to="/help/shipping" className="hover:text-[var(--gl-orange)] transition">{t("footer.help.shipping")}</Link></li>
            <li><Link to="/help/returns" className="hover:text-[var(--gl-orange)] transition">{t("footer.help.returns")}</Link></li>
          </ul>
        </div>

        {/* Boutique — real pages that exist */}
        <div>
          <div className="font-typewriter text-[10px] tracking-[0.3em] uppercase text-[#F1ECE2]/60 mb-4">{t("footer.col.about")}</div>
          <ul className="space-y-2 text-sm" data-testid="footer-boutique-links">
            <li><Link to="/about" className="hover:text-[var(--gl-orange)] transition" data-testid="footer-about-link">{t("nav.about")}</Link></li>
            {journalCount > 0 && (
              <li><Link to="/journal" className="hover:text-[var(--gl-orange)] transition">{t("nav.journal")}</Link></li>
            )}
          </ul>
        </div>
      </div>

      {/* Trust signals — four quick assurances above the payment logos */}
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          <TrustBadge testid="trust-free-shipping" title={t("footer.trust.freeshipping.title")} sub={t("footer.trust.freeshipping.sub", { threshold })}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h13l3 3v5h-2M3 15V7"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>
          </TrustBadge>
          <TrustBadge testid="trust-returns" title={t("footer.trust.returns.title")} sub={t("footer.trust.returns.sub")}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 4 3 10 9 10"/></svg>
          </TrustBadge>
          <TrustBadge testid="trust-shipping" title={t("footer.trust.shipping.title")} sub={t("footer.trust.shipping.sub")}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="6" width="15" height="12" rx="1.5"/><path d="M16 10h4l3 3v5h-7z"/><circle cx="6" cy="19" r="2"/><circle cx="19" cy="19" r="2"/></svg>
          </TrustBadge>
          <TrustBadge testid="trust-secure" title={t("footer.trust.secure.title")} sub={t("footer.trust.secure.sub")}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5z"/><path d="m9 12 2 2 4-4"/></svg>
          </TrustBadge>
        </div>
      </div>

      {/* Payment methods badge row — brand-accurate SVG logos (Visa · Mastercard · AMEX · Multibanco · MB WAY) */}
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-center gap-3 md:gap-4">
          {/* VISA — brand blue rounded rect with white VISA wordmark */}
          <PaymentBadge label="Visa" data-testid="payment-badge-visa">
            <svg viewBox="0 0 64 40" width="64" height="40" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Visa">
              <rect width="64" height="40" rx="6" fill="#1434CB"/>
              <text x="32" y="26" textAnchor="middle" fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif" fontStyle="italic" fontWeight="900" fontSize="16" fill="#FFFFFF" letterSpacing="1.2">VISA</text>
            </svg>
          </PaymentBadge>

          {/* Mastercard — black rounded rect with interlocking red + orange circles */}
          <PaymentBadge label="Mastercard" data-testid="payment-badge-mastercard">
            <svg viewBox="0 0 64 40" width="64" height="40" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Mastercard">
              <rect width="64" height="40" rx="6" fill="#111111"/>
              <circle cx="26" cy="20" r="10" fill="#EB001B"/>
              <circle cx="38" cy="20" r="10" fill="#F79E1B"/>
              {/* True Mastercard overlap arc — same-radius intersection darkened */}
              <path d="M 32 12 A 10 10 0 0 1 32 28 A 10 10 0 0 1 32 12 Z" fill="#FF5F00"/>
            </svg>
          </PaymentBadge>

          {/* AMEX — light-blue rounded rect with stacked "AM / EX" white wordmark */}
          <PaymentBadge label="American Express" data-testid="payment-badge-amex">
            <svg viewBox="0 0 64 40" width="64" height="40" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="American Express">
              <rect width="64" height="40" rx="6" fill="#1F72CD"/>
              <text x="32" y="19" textAnchor="middle" fontFamily="'Helvetica Neue',Helvetica,Arial Black,sans-serif" fontWeight="900" fontSize="12" fill="#FFFFFF" letterSpacing="0.5">AM</text>
              <text x="32" y="31" textAnchor="middle" fontFamily="'Helvetica Neue',Helvetica,Arial Black,sans-serif" fontWeight="900" fontSize="12" fill="#FFFFFF" letterSpacing="0.5">EX</text>
              {/* Thin white outline around the wordmark, per the real Amex mark */}
              <rect x="18" y="8" width="28" height="24" rx="2" fill="none" stroke="#FFFFFF" strokeWidth="1"/>
            </svg>
          </PaymentBadge>

          {/* Multibanco — white pill, blue bracketed "MB" + "Multibanco" caption */}
          <PaymentBadge label="Multibanco" data-testid="payment-badge-mb">
            <svg viewBox="0 0 64 40" width="64" height="40" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Multibanco">
              <rect x="0.5" y="0.5" width="63" height="39" rx="5.5" fill="#FFFFFF" stroke="#E7E7E7"/>
              {/* Top + bottom brackets forming the Rede MB style container */}
              <path d="M 22 11 L 22 8 L 42 8 L 42 11" fill="none" stroke="#233A99" strokeWidth="1.4" strokeLinecap="square"/>
              <path d="M 22 24 L 22 27 L 42 27 L 42 24" fill="none" stroke="#233A99" strokeWidth="1.4" strokeLinecap="square"/>
              {/* MB letters */}
              <text x="32" y="23" textAnchor="middle" fontFamily="'Helvetica Neue',Helvetica,Arial Black,sans-serif" fontWeight="900" fontSize="12" fill="#233A99" letterSpacing="0.5">MB</text>
              {/* Multibanco caption */}
              <text x="32" y="34.5" textAnchor="middle" fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif" fontWeight="700" fontSize="4.4" fill="#233A99" letterSpacing="0.4">MULTIBANCO</text>
            </svg>
          </PaymentBadge>

          {/* MB WAY — white pill, red bracketed "MB" + black "WAY" */}
          <PaymentBadge label="MB WAY" data-testid="payment-badge-mbway">
            <svg viewBox="0 0 64 40" width="64" height="40" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="MB WAY">
              <rect x="0.5" y="0.5" width="63" height="39" rx="5.5" fill="#FFFFFF" stroke="#E7E7E7"/>
              {/* Red MB brackets */}
              <path d="M 12 11 L 12 8 L 32 8 L 32 11" fill="none" stroke="#E10600" strokeWidth="1.4" strokeLinecap="square"/>
              <path d="M 12 32 L 12 29 L 32 29 L 32 32" fill="none" stroke="#E10600" strokeWidth="1.4" strokeLinecap="square"/>
              {/* MB letters */}
              <text x="22" y="26" textAnchor="middle" fontFamily="'Helvetica Neue',Helvetica,Arial Black,sans-serif" fontWeight="900" fontSize="14" fill="#111111" letterSpacing="0.5">MB</text>
              {/* WAY next to it */}
              <text x="47.5" y="25" textAnchor="middle" fontFamily="'Helvetica Neue',Helvetica,Arial,sans-serif" fontWeight="700" fontSize="10" fill="#111111" letterSpacing="0.6">WAY</text>
            </svg>
          </PaymentBadge>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#F1ECE2]/70">
          <div>{t("footer.copy").replace("{year}", year)}</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/legal/terms" className="hover:text-[var(--gl-orange)] transition" data-testid="footer-terms-link">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-[var(--gl-orange)] transition" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/legal/cookies" className="hover:text-[var(--gl-orange)] transition" data-testid="footer-cookies-link">Cookies</Link>
            <span>{t("footer.payment")}</span>
            <LanguageSwitcher variant="footer" />
          </div>
        </div>
      </div>
    </footer>
  );
};

/** Small padded slot hosting a payment-method SVG logo. Bumped to 64×40
 * to match the reference (Kaiwalu / typical Stripe checkout icons). */
const PaymentBadge = ({ children, label, ...rest }) => (
  <div
    role="img"
    aria-label={label}
    title={label}
    className="inline-flex items-center justify-center rounded-md overflow-hidden"
    style={{ width: 64, height: 40 }}
    {...rest}
  >
    {children}
  </div>
);

/** Trust-signal row cell — icon left, title + subline right. Keeps to the
 * cream/marigold palette so it reads clearly on the dark footer. */
const TrustBadge = ({ children, title, sub, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span className="w-9 h-9 shrink-0 inline-flex items-center justify-center rounded-full bg-[var(--gl-orange)] text-[var(--gl-orange-text)]">
      {children}
    </span>
    <div className="min-w-0">
      <div className="font-badge text-[11px] tracking-[0.24em] uppercase text-[#F1ECE2]">{title}</div>
      <div className="text-xs text-[#F1ECE2]/70 leading-snug">{sub}</div>
    </div>
  </div>
);

export default Footer;
